import {
    mdiCheck,
    mdiPauseCircleOutline,
    mdiPlus,
    mdiFileDocumentOutline,
    mdiAccountOutline,
    mdiAccountAlertOutline,
    mdiClockOutline,
    mdiForumOutline,
    mdiChevronRight,
    mdiChevronDown,
    mdiShieldCheckOutline,
    mdiCloseCircleOutline,
    mdiAlertCircleOutline,
} from '@mdi/js';
import Icon from '@mdi/react';
import { formatDate } from '../../services/primitives/date-utils';
import _ from 'lodash';
import * as mobx from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { Row } from 'react-table';
import { MessagePurposeType } from '../../api';
import { MessagingContext } from '../../services';
import { getOptions, Overlay, ISelectOption, PopoverItem } from '../base';
import { DataTablePresenter, ITableColumn, ITableFetchOptions } from '../data-table';
import { SelectionActionsFab } from '../data-table/components/selection-actions-fab';
import { FilterMethod, IFilterSchema } from '../data-table/components/filters/filters.models';
import { AuthorDateFilter } from '../data-table/components/column-filter';
import { ExportAllButton, ExportAllFetchOptions } from '../data-table/components/table-export/export-all-button';
import type { IRowAction } from '../data-table/components/row-actions';
import type { IColumnFiltersConfig } from '../data-table/components/filters/filters.models';
import { Avatar as AssigneeAvatar } from './assignee-picker.styles';
import { assigneeAvatarColor } from './assignee-picker';
import {
    StatusChip as TaskStatusChip,
    StatusDot as TaskStatusDot,
} from './chat-panel/chat-header.styles';

import * as S from './message-list.styles';
import {
    ensureMessagingTablePageSizeDefaults,
    MESSAGING_DEFAULT_PAGE_SIZE,
    MESSAGING_TABLE_STORAGE_KEY,
} from './messaging-table-storage';
import {
    IMessagingMessage,
    MESSAGE_EXPORT_CSV_HEADERS,
    getNickname,
    messageToCsvRow,
    getMessageClaimIds,
    normalizeClaimIds,
} from './util';
import { clearClaimScopedListing } from './claim-scoped-task-listing';
import { AdvancedMessageSearchBar } from './advanced-message-search-bar';
import { clearTaskSearchIdCaches, TaskSearchIn } from './task-search';
import {
    getGroupHeaderLabel,
    ITaskListFetchContext,
    resolveTaskGroupId,
    resolveTaskTypeFromCategoryNames,
    sortMessagesForGrouping,
    TASK_TYPE_DEFINITIONS,
    TaskGroupId,
    TaskTypeId,
} from './task-types';

export type { ITaskListFetchContext };

const getInitials = (name: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
};

const formatAuthorDate = (value: unknown): string => {
    if (!value) {
        return '—';
    }
    const parsed = new Date(value as string);
    return isNaN(parsed.getTime()) ? '—' : formatDate(parsed, 'MMM d, yyyy, h:mm a');
};

/**
 * Who and when, as plain text — name on top, date beneath.
 *
 * Replaces an avatar whose name and date only appeared in a hover tooltip: reviewing a page of tasks
 * meant hovering every row to read its date (customer request). The native title keeps the full
 * text reachable when a long name is cut off by the column width.
 */
const AuthorDateCell = React.memo<{ name: string; date?: unknown }>(({ name, date }) => {
    const when = formatAuthorDate(date);

    return (
        <S.AuthorDateCell title={`${name}\n${when}`}>
            <S.AuthorDateName>{name}</S.AuthorDateName>
            <S.AuthorDateWhen>{when}</S.AuthorDateWhen>
        </S.AuthorDateCell>
    );
});

const VISIBLE_LINKED_IDS = 1;

const stopRowClick = (event: React.SyntheticEvent): void => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
};

/**
 * One linked ID in the row; remaining count sits behind +N.
 * Click opens an Overlay with every linked ID and must not open the message panel.
 */
const LinkedIdsCell: React.FC<{ ids?: string[]; entityLabel: string }> = ({ ids, entityLabel }) => {
    const linkedIds = normalizeClaimIds(ids);
    if (linkedIds.length === 0) {
        return <span>—</span>;
    }

    const visibleLabel = linkedIds[0];
    const overflowCount = linkedIds.length - VISIBLE_LINKED_IDS;

    return (
        <S.ClaimIdsCell>
            <S.ClaimIdsVisible title={visibleLabel}>{visibleLabel}</S.ClaimIdsVisible>
            {overflowCount > 0 && (
                <S.ClaimIdsMoreWrap onClick={stopRowClick}>
                    <Overlay
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        renderButton={onOpen => (
                            <S.ClaimIdsMore
                                type="button"
                                aria-label={`${linkedIds.length} ${entityLabel}`}
                                onClick={event => {
                                    stopRowClick(event);
                                    event.preventDefault();
                                    onOpen(event.currentTarget);
                                }}
                            >
                                {`+${overflowCount}`}
                            </S.ClaimIdsMore>
                        )}
                    >
                        <S.ClaimIdsPopover onClick={stopRowClick}>
                            {linkedIds.map(id => (
                                <S.ClaimIdsTooltipItem key={id}>{id}</S.ClaimIdsTooltipItem>
                            ))}
                        </S.ClaimIdsPopover>
                    </Overlay>
                </S.ClaimIdsMoreWrap>
            )}
        </S.ClaimIdsCell>
    );
};

const TaskAssigneeAvatars = React.memo<{
    names?: string[];
    ids?: string[];
    currentUserId?: string;
}>(({ names, ids, currentUserId }) => {
    if (!names || names.length === 0) {
        return <S.EmptyAssignee>—</S.EmptyAssignee>;
    }
    return (
        <S.AssigneeAvatarRow>
            {names.slice(0, 3).map((name, i) => {
                const id = ids?.[i] || name;
                const isYou = !!currentUserId && !!ids?.[i] && ids[i] === currentUserId;
                return (
                    <AssigneeAvatar
                        key={`${id}-${i}`}
                        bg={assigneeAvatarColor(id)}
                        isYou={isYou}
                        size={26}
                        title={isYou ? `${name} (you)` : name}
                    >
                        {getInitials(name)}
                    </AssigneeAvatar>
                );
            })}
            {names.length > 3 && <S.AssigneeOverflow>+{names.length - 3}</S.AssigneeOverflow>}
        </S.AssigneeAvatarRow>
    );
});

const SubjectCell = React.memo<{
    subject?: string;
    read?: boolean;
    categories?: string[];
    preview?: string;
}>(({ subject, read, categories, preview }) => {
    const typeId = resolveTaskTypeFromCategoryNames(categories || []);
    const icon =
        typeId === 'halt'
            ? mdiPauseCircleOutline
            : typeId === 'denial'
              ? mdiCloseCircleOutline
              : typeId === 'rejection'
                ? mdiAlertCircleOutline
                : typeId === 'records'
                  ? mdiFileDocumentOutline
                  : typeId === 'patient'
                    ? mdiAccountAlertOutline
                    : typeId === 'auth'
                      ? mdiShieldCheckOutline
                      : mdiForumOutline;
    const iconTone =
        typeId === 'halt' || typeId === 'denial' || typeId === 'rejection'
            ? 'attention'
            : typeId === 'records' || typeId === 'patient' || typeId === 'auth'
              ? 'muted'
              : 'default';
    return (
        <S.SubjectWithIcon unread={!read}>
            <S.SubjectIconWrap tone={iconTone}>
                <Icon path={icon} size={0.75} />
            </S.SubjectIconWrap>
            <S.SubjectTextBlock>
                <S.SubjectText unread={!read}>{subject || '—'}</S.SubjectText>
                {preview ? <S.SubjectPreview>{preview}</S.SubjectPreview> : null}
            </S.SubjectTextBlock>
        </S.SubjectWithIcon>
    );
});

const DueDateCell = React.memo<{ value?: string; open?: boolean }>(({ value, open }) => {
    if (!value || open === false) {
        return <span />;
    }
    let label = value;
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
        label = formatDate(parsed, 'MMM d');
    }
    return (
        <S.DueDateCell>
            <Icon path={mdiClockOutline} size={0.65} />
            Due {label}
        </S.DueDateCell>
    );
});

const StatusCell = React.memo<{ open?: boolean }>(({ open }) => {
    const status = open === false ? 'done' : 'needs_you';
    const label = status === 'done' ? 'Done' : 'Needs you';
    return (
        <TaskStatusChip status={status}>
            <TaskStatusDot status={status} />
            {label}
        </TaskStatusChip>
    );
});

interface ISelectedActionsBarProps {
    presenter: DataTablePresenter<IMessagingMessage>;
    isDoneView: boolean;
    isPending: boolean;
    isReadOnly: boolean;
    showProviderDone: boolean;
    onMarkDone: () => void;
    onMarkProviderDone: () => void;
    onMarkUndone: () => void;
}

interface IMessageListNoResultsProps {
    /**
     * True when a search matched only closed tasks, we are not on Done, and Read is Show all.
     * `typeCounts.done` strips `filterRead` (chip badges), so with Unread/Read selected the
     * count can be positive for Done rows the list will still exclude.
     */
    showDoneHint: boolean;
    doneCount: number;
    onGoToDone: () => void;
}

/**
 * A body search resolves to message IDs first and is narrowed by the tab's open/closed filter
 * afterwards, so a term that only matches closed tasks empties every open tab. Answering that with
 * a bare "No results" reads as "the search is broken" — say where the matches actually are.
 */
const MessageListNoResults: React.FC<IMessageListNoResultsProps> = ({
    showDoneHint,
    doneCount,
    onGoToDone,
}) => {
    if (!showDoneHint) {
        return <S.NoResults>No results</S.NoResults>;
    }

    return (
        <S.NoResults>
            <S.NoResultsBody>
                <div>No open tasks match this search</div>
                <S.NoResultsHint>
                    {doneCount} {doneCount === 1 ? 'match is' : 'matches are'} in Done
                    <S.NoResultsAction variant="text" size="small" onClick={onGoToDone}>
                        View in Done
                    </S.NoResultsAction>
                </S.NoResultsHint>
            </S.NoResultsBody>
        </S.NoResults>
    );
};

/**
 * Batch actions for the selected tasks, on the same floating ⋮ button the claims lists use.
 * Reads selection from the presenter so checkbox clicks don't setState the whole list.
 */
const SelectedActionsBar = observer((props: ISelectedActionsBarProps) => {
    const {
        presenter,
        isDoneView,
        isPending,
        isReadOnly,
        showProviderDone,
        onMarkDone,
        onMarkProviderDone,
        onMarkUndone,
    } = props;
    const count = Object.keys(presenter.selectedIds).filter(id => presenter.selectedIds[id]).length;
    const disabled = isPending || isReadOnly;

    return (
        <SelectionActionsFab selectedCount={count} noun="task" disabled={disabled}>
            {isDoneView && (
                <PopoverItem onClick={onMarkUndone} disabled={disabled}>
                    Move to Inbox
                </PopoverItem>
            )}
            {!isDoneView && (
                <PopoverItem onClick={onMarkDone} disabled={disabled}>
                    Move to Done
                </PopoverItem>
            )}
            {!isDoneView && showProviderDone && (
                <PopoverItem onClick={onMarkProviderDone} disabled={disabled}>
                    Move to Done as Provider
                </PopoverItem>
            )}
        </SelectionActionsFab>
    );
});

interface IMessageListRedesignedProps {
    messages: IMessagingMessage[];
    total: number;
    typeCounts: Partial<Record<TaskTypeId, number>>;
    mineCount?: number;
    onFetchData: (newTableState: ITableFetchOptions, context: ITaskListFetchContext) => Promise<void>;
    onShowCompose: () => void;
    selectedMessageId: string | undefined;
    showFiltering?: boolean;
    showNavigation?: boolean;
    showSettings?: boolean;
    customColumns?: ITableColumn[];
    columnFiltersConfig?: IColumnFiltersConfig;
    rowActions?: IRowAction[];
    refreshKey?: number;
    loadAuthorOptions?: (searchTerm: string) => Promise<{ label: string; value: string }[]>;
    currentUserId?: string;
    currentUserLabel?: string;
    onExportAll?: (options: ExportAllFetchOptions<IMessagingMessage>) => Promise<void>;
    onExportAllError?: (message: string) => void;
    /** Unread count for the active view (ignores the read dropdown). */
    unreadCount?: number;
    /** Total count for the active view ignoring the read dropdown. */
    allFilteredCount?: number;
    /** When true (default), group All-types rows by known task types. */
    grouped?: boolean;
    /**
     * Offer the "Coding result" search. Off unless the app passes a `searchCodings` resolver —
     * an always-empty coding search is indistinguishable on screen from "nothing matches" (NS-4).
     */
    showCodingSearch?: boolean;
    /**
     * Offer the "Coder rule #" search (FE-126). Off where the app cannot answer it truthfully
     * (demo: generated claims carry no coder-rule runs), for the same reason as `showCodingSearch`.
     */
    showCoderRuleSearch?: boolean;
    /**
     * Offer the claim DOS range (CA-3441). Off unless the app passes a `searchClaimsByDos`
     * resolver, for the same reason as `showCodingSearch`.
     */
    showDosFilter?: boolean;
}

interface IMessageListRedesignedState {
    tableState: ITableFetchOptions | undefined;
    isPending: boolean;
    searchTerm: string;
    searchIn: TaskSearchIn;
    currentSortBy: any[];
    selectedRowId: string | null;
    typeId: TaskTypeId;
    mineOnly: boolean;
    readFilter: 'all' | 'unread' | 'read';
}

interface ITaskGroupRowProps {
    /** Collapsed batch groups in All-types view (true = collapsed). */
    collapsedGroups: mobx.ObservableMap<TaskGroupId, boolean>;
    groupId: TaskGroupId;
    showHeader: boolean;
    label: string;
    icon: string | null;
    rowElement: React.ReactElement;
    onToggle: (groupId: TaskGroupId) => void;
}

/**
 * The DataTable body is React.memo'd, so a setState in the list would never reach the row wrapper.
 * Reading the collapsed flag from an observable here subscribes this leaf directly to the toggle.
 */
const TaskGroupRow = observer((props: ITaskGroupRowProps) => {
    const { collapsedGroups, groupId, showHeader, label, icon, rowElement, onToggle } = props;
    const isCollapsed = !!collapsedGroups.get(groupId);

    if (!showHeader) {
        return isCollapsed ? null : rowElement;
    }

    return (
        <>
            <S.BatchGroupHeader
                role="button"
                tabIndex={0}
                onClick={() => onToggle(groupId)}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onToggle(groupId);
                    }
                }}
            >
                <S.BatchExpandIcon>
                    <Icon path={isCollapsed ? mdiChevronRight : mdiChevronDown} size={0.85} />
                </S.BatchExpandIcon>
                {icon && (
                    <S.BatchTypeIcon>
                        <Icon path={icon} size={0.85} />
                    </S.BatchTypeIcon>
                )}
                <S.BatchGroupTitle>{label}</S.BatchGroupTitle>
            </S.BatchGroupHeader>
            {!isCollapsed && rowElement}
        </>
    );
});

export class MessageListRedesigned extends React.Component<IMessageListRedesignedProps, IMessageListRedesignedState> {
    public static contextType = MessagingContext;
    private presenter: DataTablePresenter<IMessagingMessage>;
    private cachedColumns: ITableColumn<IMessagingMessage>[] | null = null;
    private cachedCustomColumnsRef: ITableColumn[] | undefined;
    private cachedCurrentUserId: string | undefined;
    private prevListLoading = false;
    /** True after a page-0 replace until the next listing is applied to the presenter. */
    private replaceInFlight = false;
    /**
     * DataTable also onFetches when validFilters change (500ms debounce). An explicit
     * refetchFromPageZero would otherwise be aborted by cancelGroup — first GET fails, second OK.
     */
    private skipNextTableFetch = false;
    private skipNextTableFetchTimer: number | undefined;
    private collapsedGroups = mobx.observable.map<TaskGroupId, boolean>();
    /** Group for every row in All-types view (needed so collapse hides non-header rows). */
    private groupByMessageId = new Map<string, TaskGroupId>();
    /** Message ids that should show a group header (precomputed — avoids findIndex per row). */
    private groupHeaderByMessageId = new Map<string, TaskGroupId>();
    private lastDataContentSig = '';
    /** Full grouped page; presenter.data is the collapse-filtered subset so virtual height shrinks. */
    private pageRows: IMessagingMessage[] = [];

    private get columnFiltersConfig(): IColumnFiltersConfig {
        return {
            ...(this.props.columnFiltersConfig || {}),
            ID: {
                filterType: 'String',
                filterId: 'filterId',
                placeholder: 'Search by ID...',
                defaultMethod: FilterMethod.LK,
            },
            Created: {
                filterType: 'Custom',
                component: (props: any) => (
                    <AuthorDateFilter
                        {...props}
                        loadOptions={this.props.loadAuthorOptions as any}
                        currentUserId={this.props.currentUserId}
                        currentUserLabel={this.props.currentUserLabel}
                    />
                ),
                filterId: 'filterCreatedCombined',
                label: 'Filter Created',
                defaultMethod: FilterMethod.LK,
            },
            Updated: {
                filterType: 'DateTime',
                filterId: 'filterUpdated',
                label: 'Filter Updated',
                defaultMethod: FilterMethod.GTE,
            },
        };
    }

    private get columns(): ITableColumn<IMessagingMessage>[] {
        if (
            this.cachedColumns &&
            this.cachedCustomColumnsRef === this.props.customColumns &&
            this.cachedCurrentUserId === this.props.currentUserId
        ) {
            return this.cachedColumns;
        }

        this.cachedCustomColumnsRef = this.props.customColumns;
        this.cachedCurrentUserId = this.props.currentUserId;
        const extra = (this.props.customColumns || [])
            .filter(c => c.id !== 'Assignee' && c.id !== 'Assignees')
            .map(c => (c.id === 'Categories' ? { ...c, show: false } : c));

        this.cachedColumns = [
            {
                id: 'ID',
                Header: 'ID',
                accessor: 'id',
                minWidth: 100,
                maxWidth: 300,
                show: false,
            },
            {
                id: 'Created',
                Header: 'Created',
                accessor: 'created',
                Cell: ({ row, cell }): React.ReactElement => {
                    const authorName =
                        (row.original as any).authorName || getNickname(row.original.author, true) || 'Unknown';
                    return <AuthorDateCell name={authorName} date={cell.value} />;
                },
                // Wide enough for "Sep 21, 2026, 5:34 PM" on the second line.
                minWidth: 130,
                width: 150,
                maxWidth: 220,
                show: true,
            },
            {
                id: 'Updated',
                Header: 'Updated',
                accessor: 'updated',
                Cell: ({ row, cell }): React.ReactElement => {
                    const lastAuthorName =
                        (row.original as any).lastAuthorName ||
                        getNickname(row.original.lastAuthor, true) ||
                        'Unknown';
                    return <AuthorDateCell name={lastAuthorName} date={cell.value} />;
                },
                minWidth: 130,
                width: 150,
                maxWidth: 220,
                show: true,
            },
            {
                id: 'Subject',
                Header: 'Subject',
                accessor: 'subject',
                Cell: ({ row }): React.ReactElement => (
                    <SubjectCell
                        subject={row.original.subject}
                        read={row.original.read}
                        categories={(row.original as any).categories}
                        preview={
                            (row.original as any).lastCommentPreview ||
                            (row.original as any).firstCommentBody ||
                            ''
                        }
                    />
                ),
                minWidth: 280,
                width: 420,
                maxWidth: 700,
                show: true,
            },
            {
                id: 'DueDate',
                Header: 'Due',
                accessor: 'dueDate',
                Cell: ({ cell, row }): React.ReactElement => (
                    <DueDateCell value={cell.value as string | undefined} open={row.original.open} />
                ),
                minWidth: 110,
                maxWidth: 160,
                disableSortBy: true,
                show: true,
            },
            {
                id: 'Assignees',
                Header: 'Assignees',
                accessor: 'assignees',
                Cell: ({ row }): React.ReactElement => (
                    <TaskAssigneeAvatars
                        names={(row.original as any).assignees}
                        ids={(row.original as any).assigneeIds}
                        currentUserId={this.props.currentUserId}
                    />
                ),
                minWidth: 90,
                maxWidth: 140,
                disableSortBy: true,
                show: true,
            },
            {
                id: 'Status',
                Header: 'Status',
                accessor: 'open',
                Cell: ({ row }): React.ReactElement => <StatusCell open={row.original.open} />,
                minWidth: 130,
                maxWidth: 160,
                disableSortBy: true,
                show: true,
            },
            {
                id: 'ClaimId',
                Header: 'Claim ID',
                accessor: (row: IMessagingMessage): string =>
                    getMessageClaimIds(row).slice(0, VISIBLE_LINKED_IDS).join(', '),
                Cell: ({ row }): React.ReactElement => (
                    <LinkedIdsCell
                        ids={getMessageClaimIds(row.original as IMessagingMessage)}
                        entityLabel="claim IDs"
                    />
                ),
                minWidth: 140,
                width: 180,
                maxWidth: 240,
                disableSortBy: true,
                show: true,
            },
            {
                id: 'Purpose',
                Header: 'Purpose',
                accessor: 'purposeType',
                show: false,
            },
            ...extra,
        ] as ITableColumn<IMessagingMessage>[];
        return this.cachedColumns;
    }

    private filterSchemas: IFilterSchema[] = [
        {
            label: 'ID',
            filterId: 'filterId',
            filterType: 'String',
            columnId: 'ID',
        },
        {
            label: 'Subject',
            filterId: 'filterSubject',
            filterType: 'String',
            columnId: 'Subject',
        },
        {
            label: 'Author',
            filterId: 'filterAuthor',
            filterType: 'String',
            columnId: 'Created',
        },
        {
            label: 'Updated',
            filterId: 'filterUpdated',
            filterType: 'DateTime',
            columnId: 'Updated',
        },
        {
            label: 'Created',
            filterId: 'filterCreated',
            filterType: 'DateTime',
            columnId: 'Created',
        },
        {
            label: 'Purpose',
            filterId: 'filterPurpose',
            filterType: 'Select',
            options: getOptions(MessagePurposeType, { normalize: true }),
            columnId: 'Purpose',
        },
        {
            label: 'Read',
            filterId: 'filterRead',
            filterType: 'Select',
            options: [
                { label: 'Yes', value: '1' },
                { label: 'No', value: '0' },
            ],
            columnId: '',
        },
    ];

    constructor(props: IMessageListRedesignedProps) {
        super(props);
        const tableDefaults = this.getInitialTableDefaults();
        this.state = {
            tableState: undefined,
            isPending: false,
            searchTerm: '',
            searchIn: 'subject',
            currentSortBy: tableDefaults?.sortBy || [{ id: 'Updated', desc: true }],
            selectedRowId: props.selectedMessageId || null,
            typeId: 'all',
            mineOnly: false,
            readFilter: 'all',
        };
        this.presenter = new DataTablePresenter<IMessagingMessage>({
            name: MESSAGING_TABLE_STORAGE_KEY,
            data: [],
            columns: this.columns,
            filterSchemas: this.filterSchemas,
        });
        // Ensure localStorage has a page size (default 100). Respect any saved value, including 20.
        this.presenter.onPageSizeUpdate(ensureMessagingTablePageSizeDefaults());
    }

    private getSelectedIds = (): string[] =>
        Object.keys(this.presenter.selectedIds).filter(id => this.presenter.selectedIds[id]);

    private clearSelection = (): void => {
        this.presenter.onSelectedUpdate({});
    };

    private getInitialTableDefaults = () => {
        ensureMessagingTablePageSizeDefaults();
        return new DataTablePresenter<IMessagingMessage>({
            name: MESSAGING_TABLE_STORAGE_KEY,
            data: [],
            columns: this.columns,
            filterSchemas: this.filterSchemas,
        }).getTableDefaults();
    };

    private getFetchContext = (): ITaskListFetchContext => ({
        typeId: this.state.typeId,
        mineOnly: this.state.mineOnly,
        searchTerm: this.state.searchTerm,
        searchIn: this.state.searchIn,
    });

    /** Group collapse is scoped to the rendered page, so any new fetch shows groups expanded. */
    private expandAllGroups = (): void => {
        if (this.collapsedGroups.size === 0) {
            return;
        }
        mobx.runInAction(() => {
            this.collapsedGroups.clear();
        });
    };

    private beginPageZeroReplace = (): void => {
        this.replaceInFlight = true;
        mobx.runInAction(() => {
            this.presenter.onDataUpdate([]);
            this.presenter.onDataTotalChange(0);
        });
    };

    private handleFetchData = async (tableState: ITableFetchOptions): Promise<void> => {
        if (this.skipNextTableFetch) {
            this.skipNextTableFetch = false;
            if (this.skipNextTableFetchTimer != null) {
                window.clearTimeout(this.skipNextTableFetchTimer);
                this.skipNextTableFetchTimer = undefined;
            }
            return;
        }
        this.expandAllGroups();
        if ((tableState.pageIndex || 0) === 0) {
            this.beginPageZeroReplace();
        }
        this.setState(prev => {
            const sortUnchanged =
                !tableState.sortBy || prev.currentSortBy === tableState.sortBy;
            if (prev.tableState === tableState && sortUnchanged) {
                return null;
            }
            return {
                tableState,
                currentSortBy: tableState.sortBy || prev.currentSortBy,
            };
        });
        return this.props.onFetchData(tableState, this.getFetchContext());
    };

    private refetchFromPageZero = (): void => {
        this.expandAllGroups();
        this.beginPageZeroReplace();
        const pageSize = this.presenter.controlledPageSize || MESSAGING_DEFAULT_PAGE_SIZE;
        const opts: ITableFetchOptions = {
            pageIndex: 0,
            pageSize,
            sortBy: this.state.currentSortBy,
            validFilters: this.presenter.validFilters || [],
        };
        this.props.onFetchData(opts, this.getFetchContext());
        this.skipNextTableFetch = true;
        if (this.skipNextTableFetchTimer != null) {
            window.clearTimeout(this.skipNextTableFetchTimer);
        }
        this.skipNextTableFetchTimer = window.setTimeout(() => {
            this.skipNextTableFetch = false;
            this.skipNextTableFetchTimer = undefined;
        }, 750);
    };

    public componentDidUpdate(prevProps: IMessageListRedesignedProps): void {
        const { messages, total, selectedMessageId, refreshKey, customColumns, grouped } = this.props;

        if (prevProps.selectedMessageId !== selectedMessageId) {
            this.setState({ selectedRowId: selectedMessageId || null });
        }

        if (refreshKey !== undefined && prevProps.refreshKey !== refreshKey) {
            this.refetchFromPageZero();
        }

        const hasReadFilter = !!_.find(this.presenter.filters, { filterId: 'filterRead' });
        if (!hasReadFilter && this.state.readFilter !== 'all') {
            this.setState({ readFilter: 'all' });
        }

        if (prevProps.customColumns !== customColumns) {
            this.presenter.onColumnsUpdate(this.columns);
        }

        const displayMessages: IMessagingMessage[] =
            grouped !== false && this.state.typeId === 'all'
                ? sortMessagesForGrouping(messages as Array<IMessagingMessage & { categories?: string[] }>)
                : messages || [];

        const isListLoading = !!(this.context as any)?.isListLoading;
        const finishedLoading = this.prevListLoading && !isListLoading;
        this.prevListLoading = isListLoading;

        // Listing total 0 is the source of truth for an empty page. Parent row memos can
        // keep stale mapped rows when MobX replaces an observable array in place.
        const rows = total === 0 ? [] : displayMessages;
        const incomingEmpty = rows.length === 0;
        const hasPresenterRows = this.presenter.data.length > 0;
        // Compare against the last applied page, not raw props. Grouping sorts a copy,
        // so unsorted prevProps.messages vs sorted rows is always unequal and would
        // push a new presenter array on every local setState (move-to-Done refetch).
        const prevDisplayIds = this.pageRows.map(m => m.id).join(',');
        const nextIds = rows.map(m => m.id).join(',');
        const contentSig = rows
            .map(m => `${m.id}:${m.updated || ''}:${(m as any).read}:${(m as any).open}`)
            .join(',');

        if (incomingEmpty) {
            if (hasPresenterRows || this.presenter.dataTotal !== (total ?? 0)) {
                mobx.runInAction(() => {
                    this.presenter.onDataUpdate([]);
                    this.presenter.onDataTotalChange(total ?? 0);
                });
            }
            this.groupByMessageId.clear();
            this.groupHeaderByMessageId.clear();
            this.pageRows = [];
            this.lastDataContentSig = '';
            this.replaceInFlight = false;
            return;
        }

        // After beginPageZeroReplace the presenter is []. Do not restore the previous
        // page from unchanged props before the replacement listing is applied.
        if (this.replaceInFlight && !finishedLoading && prevDisplayIds === nextIds) {
            return;
        }

        if (
            finishedLoading ||
            prevDisplayIds !== nextIds ||
            contentSig !== this.lastDataContentSig ||
            prevProps.total !== total
        ) {
            this.pageRows = rows;
            this.rebuildGroupHeaders(rows, grouped !== false && this.state.typeId === 'all');
            this.lastDataContentSig = contentSig;
            this.applyVisiblePageRows(total);
            this.replaceInFlight = false;
        }
    }

    private getVisiblePageRows = (): IMessagingMessage[] => {
        if (this.collapsedGroups.size === 0) {
            return this.pageRows;
        }

        return this.pageRows.filter(message => {
            const id = message.id;
            if (!id) {
                return true;
            }
            const group = this.groupByMessageId.get(id);
            if (!group || !this.collapsedGroups.get(group)) {
                return true;
            }
            return this.groupHeaderByMessageId.has(id);
        });
    };

    private applyVisiblePageRows = (total: number): void => {
        mobx.runInAction(() => {
            this.presenter.onDataUpdate(this.getVisiblePageRows());
            this.presenter.onDataTotalChange(total);
        });
    };

    private rebuildGroupHeaders = (messages: IMessagingMessage[], grouped: boolean): void => {
        this.groupByMessageId.clear();
        this.groupHeaderByMessageId.clear();
        if (!grouped) {
            return;
        }
        let prevGroup: TaskGroupId | null = null;
        for (const message of messages) {
            const id = message.id;
            if (!id) {
                continue;
            }
            const group = resolveTaskGroupId((message as any).categories || []);
            this.groupByMessageId.set(id, group);
            if (group !== prevGroup) {
                this.groupHeaderByMessageId.set(id, group);
                prevGroup = group;
            }
        }
    };

    public componentWillUnmount(): void {
        clearTaskSearchIdCaches();
        // A DOS / Payer collection is module-level and outlives the page unless stopped here.
        clearClaimScopedListing();
        if (this.skipNextTableFetchTimer != null) {
            window.clearTimeout(this.skipNextTableFetchTimer);
        }
    }

    /**
     * Stable identity on purpose — the table takes this as a component type, and a fresh arrow per
     * render would remount the empty state.
     */
    private renderNoResults = (): React.ReactElement => {
        const doneCount = this.props.typeCounts.done ?? 0;
        return (
            <MessageListNoResults
                showDoneHint={
                    this.hasActiveSearch() &&
                    this.state.typeId !== 'done' &&
                    this.state.readFilter === 'all' &&
                    doneCount > 0
                }
                doneCount={doneCount}
                onGoToDone={this.goToDoneView}
            />
        );
    };

    private goToDoneView = (): void => {
        this.handleTypeChange('done');
    };

    private hasActiveSearch = (): boolean => this.state.searchTerm.trim().length > 0;

    private handleTypeChange = (typeId: TaskTypeId): void => {
        if (typeId === this.state.typeId) {
            return;
        }
        this.clearSelection();
        this.setState({ typeId }, () => {
            this.refetchFromPageZero();
        });
    };

    private handleMineToggle = (): void => {
        this.clearSelection();
        this.setState({ mineOnly: !this.state.mineOnly }, () => {
            this.refetchFromPageZero();
        });
    };

    private getReadFilterOptions = (): ISelectOption[] => {
        const totalCount = this.props.allFilteredCount ?? this.props.total;
        const unreadCount = this.props.unreadCount ?? 0;
        const readCount = Math.max(0, totalCount - unreadCount);

        return [
            { value: 'all', label: `Show all (${totalCount})` },
            { value: 'unread', label: `Show unread (${unreadCount})` },
            { value: 'read', label: `Show read (${readCount})` },
        ];
    };

    private handleReadFilterChange = (newValue: ISelectOption | readonly ISelectOption[] | null): void => {
        if (Array.isArray(newValue) || !newValue) {
            return;
        }

        const option = newValue as ISelectOption;
        const value = (option.value || 'all') as 'all' | 'unread' | 'read';
        this.setState({ readFilter: value });

        if (value === 'unread') {
            this.presenter.findAndUpdateFilter(
                { filterId: 'filterRead' },
                {
                    filterId: 'filterRead',
                    filterType: 'Select',
                    filterMethod: FilterMethod.EQ,
                    value: { label: 'No', value: '0' },
                },
            );
        } else if (value === 'read') {
            this.presenter.findAndUpdateFilter(
                { filterId: 'filterRead' },
                {
                    filterId: 'filterRead',
                    filterType: 'Select',
                    filterMethod: FilterMethod.EQ,
                    value: { label: 'Yes', value: '1' },
                },
            );
        } else {
            this.presenter.findAndRemoveFilter({ filterId: 'filterRead' });
        }
    };

    private handleSearchCommit = (term: string, searchIn: TaskSearchIn = 'subject'): void => {
        if (term === this.state.searchTerm && searchIn === this.state.searchIn) {
            this.refetchFromPageZero();
            return;
        }
        if (searchIn !== this.state.searchIn) {
            clearTaskSearchIdCaches();
        }
        this.setState({ searchTerm: term, searchIn }, () => {
            this.refetchFromPageZero();
        });
    };

    private typeIcon = (typeId: TaskTypeId | TaskGroupId): string | null => {
        if (typeId === 'halt') return mdiPauseCircleOutline;
        if (typeId === 'denial') return mdiCloseCircleOutline;
        if (typeId === 'rejection') return mdiAlertCircleOutline;
        if (typeId === 'records') return mdiFileDocumentOutline;
        if (typeId === 'patient') return mdiAccountAlertOutline;
        if (typeId === 'auth') return mdiShieldCheckOutline;
        if (typeId === 'done') return mdiCheck;
        if (typeId === 'other') return mdiForumOutline;
        return null;
    };

    private toggleGroupCollapsed = (groupId: TaskGroupId): void => {
        mobx.runInAction(() => {
            this.collapsedGroups.set(groupId, !this.collapsedGroups.get(groupId));
            this.presenter.onDataUpdate(this.getVisiblePageRows());
        });
    };

    private renderFiltersBar = (): React.ReactElement => {
        const { typeId, mineOnly, readFilter } = this.state;
        const { typeCounts, mineCount } = this.props;
        const isListLoading = (this.context as any)?.isListLoading;
        const readFilterOptions = this.getReadFilterOptions();

        return (
            <S.TaskToolbar>
                <S.MineTasksButton active={mineOnly} onClick={this.handleMineToggle} type="button">
                    <Icon path={mdiAccountOutline} size={0.7} />
                    My tasks
                    {typeof mineCount === 'number' && (
                        <S.TypeCountBadge active={mineOnly}>{mineCount}</S.TypeCountBadge>
                    )}
                </S.MineTasksButton>

                <S.TypeFilterBar>
                    {TASK_TYPE_DEFINITIONS.map(def => {
                        const isActive = typeId === def.id;
                        const count = typeCounts[def.id];
                        const icon = this.typeIcon(def.id);
                        const showCount = def.id === 'all' || def.id === 'done';
                        return (
                            <S.TypeFilterChip
                                key={def.id}
                                active={isActive}
                                onClick={() => this.handleTypeChange(def.id)}
                                type="button"
                                disabled={isListLoading}
                            >
                                {icon && <Icon path={icon} size={0.65} />}
                                {def.label}
                                {showCount && typeof count === 'number' && (
                                    <S.TypeCountBadge active={isActive}>{count}</S.TypeCountBadge>
                                )}
                            </S.TypeFilterChip>
                        );
                    })}
                </S.TypeFilterBar>

                <S.TaskToolbarRight>
                    <S.ReadFilterSelectWrapper>
                        <S.ReadFilterSelect
                            options={readFilterOptions}
                            value={readFilterOptions.find(opt => opt.value === readFilter)}
                            onChange={this.handleReadFilterChange}
                            isSearchable={false}
                            placeholder="Filter messages"
                            required={true}
                            isDisabled={isListLoading}
                        />
                    </S.ReadFilterSelectWrapper>
                </S.TaskToolbarRight>
            </S.TaskToolbar>
        );
    };

    private renderControlsExtra = (): React.ReactElement => (
        <>
            <S.NavSearchCluster>
                <AdvancedMessageSearchBar
                    searchTerm={this.state.searchTerm}
                    searchIn={this.state.searchIn}
                    presenter={this.presenter}
                    columnFiltersConfig={this.props.columnFiltersConfig}
                    loadAuthorOptions={this.props.loadAuthorOptions}
                    currentUserId={this.props.currentUserId}
                    currentUserLabel={this.props.currentUserLabel}
                    onSearchCommit={this.handleSearchCommit}
                    showCodingSearch={this.props.showCodingSearch}
                    showCoderRuleSearch={this.props.showCoderRuleSearch}
                    showDosFilter={this.props.showDosFilter}
                />
            </S.NavSearchCluster>
            {this.props.onExportAll && (
                <ExportAllButton
                    total={(this.props.typeCounts.all || 0) + (this.props.typeCounts.done || 0)}
                    scopes={[
                        { value: 'open', label: 'Open' },
                        { value: 'done', label: 'Done' },
                        { value: 'all', label: 'All' },
                    ]}
                    onFetchAll={this.props.onExportAll}
                    toCsvRow={messageToCsvRow}
                    headers={[...MESSAGE_EXPORT_CSV_HEADERS]}
                    filename={`messages-all-${new Date().toISOString().slice(0, 10)}`}
                    exportTitle="Exporting all messages"
                    onError={this.props.onExportAllError}
                />
            )}
        </>
    );

    private renderRowWrapper = (row: Row<IMessagingMessage>, renderRow: React.ReactElement): React.ReactElement => {
        if (this.props.grouped === false || this.state.typeId !== 'all') {
            return renderRow;
        }
        const id = row.original.id;
        const groupId = id ? this.groupByMessageId.get(id) : undefined;
        if (!groupId) {
            return renderRow;
        }

        return (
            <TaskGroupRow
                collapsedGroups={this.collapsedGroups}
                groupId={groupId}
                showHeader={!!id && this.groupHeaderByMessageId.has(id)}
                label={getGroupHeaderLabel(groupId)}
                icon={this.typeIcon(groupId)}
                rowElement={renderRow}
                onToggle={this.toggleGroupCollapsed}
            />
        );
    };

    public render(): React.ReactElement {
        const { onShowCompose, typeCounts } = this.props;
        const openNeedsAction = typeCounts.all ?? 0;
        const isDoneView = this.state.typeId === 'done';

        return (
            <S.MessageListContainer>
                <S.MessageHeader>
                    <S.HeaderTop>
                        <S.TitleGroup>
                            <S.Title>Tasks</S.Title>
                            <S.TaskSubtitle>
                                {openNeedsAction > 0
                                    ? `${openNeedsAction} ${openNeedsAction === 1 ? 'task needs your action' : 'tasks need your action'}`
                                    : this.hasActiveSearch()
                                      ? 'No open tasks match this search.'
                                      : 'All caught up — nothing open.'}
                            </S.TaskSubtitle>
                        </S.TitleGroup>
                        <S.HeaderActions>
                            <S.PrimaryButton disabled={(this.context as any).isReadOnly} onClick={onShowCompose}>
                                <Icon path={mdiPlus} size={0.7} />
                                <span>Create task</span>
                            </S.PrimaryButton>
                        </S.HeaderActions>
                    </S.HeaderTop>

                    {this.renderFiltersBar()}
                </S.MessageHeader>

                <S.TableCard>
                    <S.MessagingTable
                        presenter={this.presenter}
                        isLoading={(this.context as any).isListLoading}
                        onFetch={this.handleFetchData}
                        onRowClick={this.handleRowClick}
                        rowClass={this.getRowClass}
                        hideFiltering={true}
                        hideHeader={false}
                        hideSettings={false}
                        hideNavigation={false}
                        showExport={true}
                        exportFilename={`messages-${new Date().toISOString().slice(0, 10)}`}
                        selectableRows={true}
                        columnFiltersConfig={this.columnFiltersConfig}
                        renderRowWrapper={this.renderRowWrapper}
                        renderControlsExtra={this.renderControlsExtra}
                        NotFoundComponent={this.renderNoResults}
                        virtualized={true}
                        estimateRowHeight={80}
                    />
                </S.TableCard>

                <SelectedActionsBar
                    presenter={this.presenter}
                    isDoneView={isDoneView}
                    isPending={this.state.isPending}
                    isReadOnly={!!(this.context as any).isReadOnly}
                    showProviderDone={!!(this.context as any).onProviderMarkAsClosed}
                    onMarkDone={this.markSelectedDone}
                    onMarkProviderDone={this.markSelectedProviderDone}
                    onMarkUndone={this.markSelectedUndone}
                />

                <S.SelectedMessageRow show={!!this.props.selectedMessageId} />
            </S.MessageListContainer>
        );
    }

    private getRowClass = (row: Row<IMessagingMessage>): string => {
        const classes: string[] = [];
        if (row.original.id === this.props.selectedMessageId) {
            classes.push('selected-message');
        }
        if (!row.original.read) {
            classes.push('unread-message');
        }
        return classes.join(' ');
    };

    private handleRowClick = async (row: Row<IMessagingMessage>): Promise<void> => {
        const { onMessageClose, onMessageOpen } = this.context as any;
        const { selectedMessageId } = this.props;
        const { original: message } = row;
        const id = message.id;
        if (id === selectedMessageId) {
            await onMessageClose();
        } else {
            await (onMessageOpen && onMessageOpen(message));
        }
    };

    private markSelectedDone = async (): Promise<void> => {
        const selected = this.getSelectedIds();
        this.setState({ isPending: true });
        try {
            if (_.includes(selected, this.props.selectedMessageId)) {
                await (this.context as any).onMessageClose();
            }
            await (this.context as any).onMarkAsClosed(selected);
            this.clearSelection();
            this.setState({ isPending: false });
            this.refetchFromPageZero();
        } catch (err) {
            this.setState({ isPending: false });
        }
    };

    private markSelectedUndone = async (): Promise<void> => {
        const selected = this.getSelectedIds();
        this.setState({ isPending: true });
        try {
            await (this.context as any).onMarkAsOpen(selected);
            this.clearSelection();
            this.setState({ isPending: false });
            this.refetchFromPageZero();
        } catch (err) {
            this.setState({ isPending: false });
        }
    };

    private markSelectedProviderDone = async (): Promise<void> => {
        const context: any = this.context as any;
        if (!context.onProviderMarkAsClosed) {
            return;
        }
        const selected = this.getSelectedIds();
        this.setState({ isPending: true });
        try {
            if (_.includes(selected, this.props.selectedMessageId)) {
                await context.onMessageClose();
            }
            const ids = _.concat([], selected);
            await Promise.all(
                _.map(ids, async (id: string) => {
                    await context.onProviderMarkAsClosed(id);
                }),
            );
            this.clearSelection();
            this.setState({ isPending: false });
            this.refetchFromPageZero();
        } catch (err) {
            this.setState({ isPending: false });
        }
    };
}
